import { router } from '@inertiajs/react'
import { TableRow, TableCell } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { MoreHorizontal, Copy, ArrowUpRight, Check } from 'lucide-react'

export interface Artifact {
  id: string
  title: string
  slug: string
  kind: string
  status: string
  description: string | null
  digital_worker_name: string | null
  public_url: string | null
  view_url: string
  published_at: string | null
  created_at: string
  updated_at: string
}

interface Props {
  artifact: Artifact
  copiedId: string | null
  canManage: boolean
  onCopy: (artifact: Artifact) => void
  onDelete: (id: string) => void
}

const KIND_LABELS: Record<string, string> = {
  page: 'Página', dashboard: 'Dashboard', report: 'Relatório', proposal: 'Proposta', status: 'Status',
}
const STATUS_LABELS: Record<string, string> = {
  draft: 'Rascunho', published: 'Publicado', archived: 'Arquivado',
}
// Chips de status nos tokens semânticos do DS (Iteração 1).
const STATUS_CLASSES: Record<string, string> = {
  draft: 'border-transparent bg-secondary text-muted-foreground',
  published: 'border-transparent bg-success/12 text-success',
  archived: 'border-transparent bg-warning/13 text-warning',
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })
}

function changeStatus(id: string, status: string) {
  router.put(`/dashboard/artifacts/${id}`, { status })
}

export default function ArtifactRow({ artifact, copiedId, canManage, onCopy, onDelete }: Props) {
  const isCopied = copiedId === artifact.id

  return (
    <TableRow
      className={artifact.status === 'published' ? 'cursor-pointer' : undefined}
      onClick={artifact.status === 'published' ? (e) => {
        if ((e.target as HTMLElement).closest('button, a, [role="menuitem"]')) return
        window.open(artifact.view_url, '_blank', 'noopener,noreferrer')
      } : undefined}
    >
      <TableCell className="whitespace-normal py-3">
        <div className="min-h-[2.5rem] flex flex-col justify-center">
        <div className="flex items-center gap-2">
          <Badge variant="secondary">{KIND_LABELS[artifact.kind]}</Badge>
          {artifact.status === 'published' ? (
            <a href={artifact.view_url} target="_blank" rel="noopener noreferrer" className="font-medium hover:underline inline-flex items-center gap-1">
              {artifact.title}
              <ArrowUpRight className="size-3 text-muted-foreground" />
            </a>
          ) : (
            <span className="font-medium">{artifact.title}</span>
          )}
        </div>
        {artifact.description && (
          <div className="text-xs text-muted-foreground line-clamp-1 mt-1.5">{artifact.description}</div>
        )}
        <div className="sm:hidden flex items-center gap-2 mt-2">
          <Badge variant="outline" className={STATUS_CLASSES[artifact.status]}>{STATUS_LABELS[artifact.status]}</Badge>
        </div>
        <div className="md:hidden text-xs text-muted-foreground mt-1.5">
          {artifact.digital_worker_name || '—'} · <span className="font-mono text-[11px]">{formatDate(artifact.updated_at)}</span>
        </div>
        </div>
      </TableCell>
      <TableCell className="hidden md:table-cell text-muted-foreground">
        {artifact.digital_worker_name || '—'}
      </TableCell>
      <TableCell className="hidden md:table-cell font-mono text-[11.5px] text-muted-foreground">
        {formatDate(artifact.updated_at)}
      </TableCell>
      <TableCell className="hidden sm:table-cell">
        <Badge variant="outline" className={STATUS_CLASSES[artifact.status]}>{STATUS_LABELS[artifact.status]}</Badge>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          {artifact.status === 'published' && artifact.public_url && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon-sm" aria-label={isCopied ? 'URL copiada' : 'Copiar URL'} onClick={() => onCopy(artifact)}>
                  {isCopied ? <Check className="size-4" /> : <Copy className="size-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{isCopied ? 'Copiado!' : 'Copiar URL'}</TooltipContent>
            </Tooltip>
          )}
          {canManage && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm" aria-label="Ações">
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {artifact.status !== 'published' && (
                  <DropdownMenuItem onSelect={() => changeStatus(artifact.id, 'published')}>Publicar</DropdownMenuItem>
                )}
                {artifact.status !== 'draft' && (
                  <DropdownMenuItem onSelect={() => changeStatus(artifact.id, 'draft')}>Marcar como rascunho</DropdownMenuItem>
                )}
                {artifact.status !== 'archived' && (
                  <DropdownMenuItem onSelect={() => changeStatus(artifact.id, 'archived')}>Arquivar</DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="destructive" onSelect={() => onDelete(artifact.id)}>Excluir</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </TableCell>
    </TableRow>
  )
}
